import { Component, ElementRef, inject, Input, OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { KeyFramesService } from '../../../core/records/data-services/keyframes.service';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { ShIconModule } from '../icon/icon.module';
import { ShImageViewerComponent } from '../image-viewer/image-viewer.component';

export * from '../image-viewer/entities/image-viewer-translations.entity';

@Component({
    standalone: true,
    selector: 'sh-document-viewer',
    templateUrl: './document-viewer.component.html',
    styleUrls: ['./document-viewer.component.scss'],
    imports: [CommonModule, ShIconModule, InfiniteScrollDirective, TranslateModule]
})
export class ShDocumentViewerComponent extends ShImageViewerComponent implements OnInit {
    private readonly minScale = 0.5;
    private readonly maxScale = 4;
    public readonly MIN_PAGES: number = 8;

    public record$: ReplaySubject<ApiFragment> = new ReplaySubject<ApiFragment>(1);
    public keyframes$: Observable<string[]>;
    public cappedKeyFrames$: Observable<string[]>;
    public pagesLoaded$: BehaviorSubject<number> = new BehaviorSubject<number>(this.MIN_PAGES);

    private scrollContainer: HTMLElement | null;

    @Input()
    set record(record: ApiFragment) {
        this.reset();
        this.record$.next(record);
    }

    private readonly keyFramesService: KeyFramesService = inject(KeyFramesService);
    private readonly hostEl: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

    ngOnInit() {
        this.initKeyFrames();

        super.ngOnInit();

        this.scrollContainer = this.findScrollContainer();
    }

    private findScrollContainer(): HTMLElement | null {
        let el: HTMLElement | null = this.hostEl.nativeElement.parentElement;
        while (el) {
            if (el.classList.contains('sh-dialog__content')) {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    }

    private initKeyFrames() {
        this.keyframes$ = this.record$.pipe(
            switchMap(record => this.keyFramesService.getKeyFrames(record.getRecordId())),
            map(keyFrames => keyFrames.map(keyFrame => keyFrame.PreviewImagePath))
        );

        // Limit the number of images loaded at once to 5 since the zoom starts getting unstable if we load to many at a time
        this.cappedKeyFrames$ = combineLatest([this.keyframes$, this.pagesLoaded$]).pipe(
            map(([keyFrames, pagesLoaded]) => keyFrames.slice(Math.max(0, pagesLoaded - this.MIN_PAGES), pagesLoaded))
        );
    }

    override reset(): void {
        super.reset();

        this.pagesLoaded$.next(this.MIN_PAGES);
        this.scrollContainer?.scrollTo({ top: 0, behavior: 'instant' });
    }

    protected override initDragging() {
        this.draggable = false;
    }

    override zoomIn() {
        if (!this.scrollContainer) {
            return;
        }

        this.transition = true;
        if (Math.round(this.scale * 10) / 10 < this.maxScale) {
            this.scale = Math.round((this.zoomFactor + this.scale) * 10) / 10;

            this.updateStyle();
        }
    }

    override zoomOut() {
        if (!this.scrollContainer) {
            return;
        }

        this.transition = true;
        if (Math.round(this.scale * 10) / 10 > this.minScale) {
            this.scale -= this.zoomFactor;

            this.updateStyle();
        }
    }

    protected updateStyle(): void {
        this.style['--scale'] = this.scale;
        this.style['--rotation'] = `${this.rotation % 360}deg`;
    }

    public onScrollDown(): void {
        this.keyframes$.pipe(take(1)).subscribe(keyframes => {
            this.pagesLoaded$.next(Math.min(this.pagesLoaded$.value + 1, keyframes.length));
        });
    }

    public onScrollUp(): void {
        this.pagesLoaded$.next(Math.max(this.MIN_PAGES, this.pagesLoaded$.value - 1));
    }

    protected rescaleImgToFit(): void {
        this.scale = 1;
    }
}
